import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { usage100PercentAPI } from '../services/api';
import PageHero from '../components/Common/PageHero';
import MetricCard from '../components/Common/MetricCard';
import DataGrid from '../components/Common/DataGrid';

interface Usage100PercentForm {
  id: number;
  formId: number;
  customerName: string;
  customerId?: string;
  serviceLine: string;
  kitNumber?: string;
  packageLimit: number;
  currentUsage: number;
  usagePercentage: number;
  accountManager?: string;
  reachedAt?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
}

const formatNumber = (num: number): string => num.toLocaleString('en-US');

const Usage100Percent: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState('all');

  const { data: forms = [], isLoading, error, refetch } = useQuery<Usage100PercentForm[]>(
    'usage-100-percent',
    () => usage100PercentAPI.getAll(),
    { refetchInterval: 30000 }
  );

  const { data: stats } = useQuery('usage-100-percent-stats', () => usage100PercentAPI.getStatistics());

  const filteredForms = forms.filter((form) => {
    const matchesSearch =
      form.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.serviceLine.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.kitNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.accountManager?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && form.isActive) ||
      (filterStatus === 'inactive' && !form.isActive);

    let matchesMonth = true;
    let matchesYear = true;
    if (form.reachedAt) {
      const reachedDate = new Date(form.reachedAt);
      const reachedMonth = reachedDate.getMonth() + 1;
      const reachedYear = reachedDate.getFullYear();
      if (filterMonth !== 'all') matchesMonth = reachedMonth === parseInt(filterMonth, 10);
      if (filterYear !== 'all') matchesYear = reachedYear === parseInt(filterYear, 10);
    } else if (filterMonth !== 'all' || filterYear !== 'all') {
      matchesMonth = false;
      matchesYear = false;
    }

    return matchesSearch && matchesStatus && matchesMonth && matchesYear;
  });

  const handleExport = async () => {
    const csvContent = [
      ['CustomerName', 'CustomerID', 'ServiceLine', 'KitNumber', 'PackageLimit', 'CurrentUsage', 'UsagePercentage', 'AccountManager', 'ReachedAt', 'Notes', 'IsActive'],
      ...filteredForms.map((form) => [
        form.customerName,
        form.customerId || '',
        form.serviceLine,
        form.kitNumber || '',
        String(form.packageLimit),
        String(form.currentUsage),
        `${Number(form.usagePercentage).toFixed(1)}%`,
        form.accountManager || '',
        form.reachedAt ? new Date(form.reachedAt).toISOString() : '',
        form.notes || '',
        form.isActive ? 'Yes' : 'No',
      ]),
    ]
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `100_percent_usage_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-gray-500">Loading 100% usage forms...</div>;
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-6xl text-red-600">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <p className="font-medium text-gray-600">Failed to load 100% usage forms</p>
          <button onClick={() => refetch()} className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'Critical usage', tone: 'red' }}
        title="100% Usage"
        description="Service lines that have reached or exceeded their package limit, ready for investigation and workspace drill-down."
        stats={[
          { icon: 'fas fa-exclamation-triangle', label: `${filteredForms.length} records` },
          { icon: 'fas fa-clock', label: `Updated ${new Date().toLocaleTimeString()}` },
        ]}
        actions={
          <>
            <button onClick={() => navigate('/forms')} className="rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white hover:bg-white/15">
              Back to Pool Customers
            </button>
            <button onClick={handleExport} className="rounded-2xl bg-white px-5 py-3 font-semibold text-slate-900 hover:bg-slate-100">
              Export
            </button>
          </>
        }
      />

      {stats && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <MetricCard label="Total records" value={stats.total} hint="All 100% usage records" icon="fas fa-file-alt" accent="red" />
          <MetricCard label="Active" value={stats.active} hint="Still active service lines" icon="fas fa-check-circle" accent="green" />
          <MetricCard label="This month" value={stats.thisMonth} hint="Reached cap this month" icon="fas fa-calendar" accent="amber" />
          <MetricCard label="This week" value={stats.thisWeek} hint="Reached cap this week" icon="fas fa-clock" accent="blue" />
        </div>
      )}

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
          <input
            type="text"
            className="rounded-2xl bg-white px-5 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-500/30 lg:col-span-2"
            placeholder="Search customers, service lines, kit numbers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-2xl bg-white px-4 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-500/30">
            <option value="all">All Records</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
          <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="rounded-2xl bg-white px-4 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-500/30">
            <option value="all">All Months</option>
            {Array.from({ length: 12 }, (_, index) => (
              <option key={index + 1} value={String(index + 1)}>
                {new Date(2026, index, 1).toLocaleString('en-US', { month: 'long' })}
              </option>
            ))}
          </select>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="rounded-2xl bg-white px-4 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-500/30">
            <option value="all">All Years</option>
            <option value="2026">2026</option>
            <option value="2025">2025</option>
          </select>
        </div>
      </div>

      <DataGrid
        title="Critical records"
        description={`${filteredForms.length} of ${forms.length} records`}
        rows={filteredForms}
        emptyText="No 100% usage records match the current filters."
        minWidthClassName="min-w-[1100px]"
        columns={[
          {
            key: 'customer',
            header: 'Customer',
            render: (form) => (
              <div>
                <div className="font-bold text-gray-900">{form.customerName}</div>
                <div className="text-sm text-gray-500">{form.customerId || 'No customer ID'}</div>
              </div>
            ),
          },
          {
            key: 'serviceLine',
            header: 'Service Line',
            render: (form) => (
              <div>
                <div className="font-semibold text-gray-900">{form.serviceLine}</div>
                <div className="text-sm text-gray-500">{form.kitNumber ? `Kit: ${form.kitNumber}` : 'No kit number'}</div>
              </div>
            ),
          },
          { key: 'usage', header: 'Usage', render: (form) => `${formatNumber(form.currentUsage)} / ${formatNumber(form.packageLimit)} GB` },
          { key: 'pct', header: 'Usage %', render: (form) => <span className="font-bold text-red-600">{Number(form.usagePercentage).toFixed(1)}%</span> },
          { key: 'manager', header: 'Account Manager', render: (form) => form.accountManager || 'Unassigned' },
          { key: 'reachedAt', header: 'Reached At', render: (form) => form.reachedAt ? new Date(form.reachedAt).toLocaleString() : 'N/A' },
          { key: 'status', header: 'Status', render: (form) => <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${form.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{form.isActive ? 'Active' : 'Inactive'}</span> },
          {
            key: 'actions',
            header: 'Actions',
            render: (form) => (
              <button onClick={() => navigate(`/forms/${form.formId}/workspace`)} className="rounded-xl p-2 text-blue-600 hover:bg-blue-50 hover:text-blue-800" title="Open Workspace">
                <i className="fas fa-arrow-up-right-from-square"></i>
              </button>
            ),
          },
        ]}
      />
    </div>
  );
};

export default Usage100Percent;
